import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import { useAuth } from '../../contexts/AuthContext'
import AuthPromptModal from './AuthPromptModal'
import USER_TYPE from '../../constants/userType'
import DisplayForUserType from '../auth/DisplayForUserType'

const guestMenuItems = [
  { link: '/', title: 'Home' },
  { link: '/products', title: 'Products' },
  { link: '/bids', title: 'Product Bidding', requiresAuth: true },
  { link: '/categories', title: 'Categories' },
  { link: '/messages', title: 'Messages', requiresAuth: true },
  { link: '/retailers', title: 'Retailers' },
  { link: '/wholesellers', title: 'Wholesalers' },
  { link: '/product-finder', title: 'Product Finder', requiresAuth: true },
  { link: '/support', title: 'Support', requiresAuth: true },
]

const customerMenuItems = [
  { link: '/favorites', title: 'Favorites' },
  { link: '/messages', title: 'Messages', requiresAuth: true },
  { link: '/product-finder', title: 'Product Finder' },
  { link: '/products', title: 'Products' },
  { link: '/bids', title: 'Product Bidding' },
  { link: '/categories', title: 'Categories' },
  { link: '/retailers', title: 'Retailers' },
  { link: '/wholesellers', title: 'Wholesalers' },
  { link: '/support', title: 'Support' },
]

const retailerMenuItems = [
  { link: '/my-shop', title: 'My shop' },
  { link: '/my-products', title: 'My products' },
  { link: '/subscription', title: 'Subscriptions' },
  { link: '/retailer/product-requests', title: 'Product requests' },
  { link: '/messages', title: 'Messages' },
  { link: '/wholesellers', title: 'Wholesalers' },
  { link: '/support', title: 'Support' },
]

const wholesellerMenuItems = [
  { link: '/my-shop', title: 'My shop' },
  { link: '/my-products', title: 'My Products' },
  { link: '/subscription', title: 'Subscriptions' },
  { link: '/messages', title: 'Messages' },
  { link: '/retailers', title: 'Retailers' },
  { link: '/support', title: 'Support' },
]

const adminMenuItems = [
  { link: '/admin/customers', title: 'Customer Management' },
  { link: '/admin/retailers', title: 'Retailers' },
  { link: '/admin/wholesellers', title: 'Wholesellers' },
  { link: '/admin/shop-verifications', title: 'Shop Verifications' },
  { link: '/admin/subscriptions', title: 'Subscriptions' },
  { link: '/admin/promotions', title: 'Promotions' },
  { link: '/admin/support', title: 'Support' },
  { link: '/admin/moderation', title: 'Moderation' },
]

export default function SideMenu({ open, onClose }) {
  const { isAuthenticated, userType, logout } = useAuth()
  const navigate = useNavigate()

  let menuItems = guestMenuItems
  if (isAuthenticated()) {
    switch (userType) {
      case USER_TYPE.customer:
        menuItems = customerMenuItems
        break
      case USER_TYPE.retailer:
        menuItems = retailerMenuItems
        break
      case USER_TYPE.wholeseller:
        menuItems = wholesellerMenuItems
        break
      case USER_TYPE.admin:
        menuItems = adminMenuItems
        break
      default:
        menuItems = guestMenuItems
    }
  } else {
    menuItems = guestMenuItems
  }

  const handleMenuClick = (item) => {
    navigate(item.link)
    onClose()
  }

  const renderMenuItem = (item) => {
    const content = (
      <div
        key={item.title}
        onClick={() => handleMenuClick(item)}
        className={`w-full flex justify-between items-center px-2 py-3 border-b border-gray-300 text-lg text-black font-inter font-normal cursor-pointer hover:bg-neutral-200-50 transition-colors duration-200 ${
          !isAuthenticated() && item.requiresAuth ? 'opacity-60' : 'opacity-100'
        }`}
      >
        {item.title}
        <ChevronRightIcon className="w-6 h-6 text-gray-500" />
      </div>
    )

    if (item?.requiresAuth) {
      return <AuthPromptModal key={item.title}>{content}</AuthPromptModal>
    }

    return content
  }

  const handleLogout = async () => {
    await logout()
  }

  return (
    <>
      <div
        className={`fixed top-0 left-0 z-[1000] h-screen transition-transform w-11/12 max-w-2xl duration-300 bg-background shadow-xl ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header with Logo and Close Button */}
        <div className="relative w-full flex items-center justify-center bg-header-bg border border-secondary-600 shadow-header h-24 px-4">
          <img src="/eastsoko-logo.png" alt="Logo" className="h-12 w-auto object-contain" />
          <button
            onClick={onClose}
            className="absolute top-1/2 right-4 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-black text-2xl font-bold hover:bg-neutral-200-100 rounded-full transition-colors duration-200"
          >
            ×
          </button>
        </div>

        {/* Authentication Buttons for Guests */}
        {!isAuthenticated() && (
          <div className="flex gap-4 mt-10 px-4">
            <button
              className="flex-1 h-12 bg-gradient-to-r from-[#990556] to-[#BC056A] rounded text-white font-medium text-lg hover:shadow-lg transition-all duration-200"
              onClick={() => {
                navigate('/login')
                onClose()
              }}
            >
              LOGIN
            </button>
            <button
              className="flex-1 h-12 bg-gradient-to-r from-[#055299] to-[#0C74D6] rounded text-white font-medium text-lg hover:shadow-lg transition-all duration-200"
              onClick={() => {
                navigate('/signup')
                onClose()
              }}
            >
              SIGN UP
            </button>
          </div>
        )}

        {/* Menu Items */}
        <div className="mt-8 px-1">{menuItems.map((item) => renderMenuItem(item))}</div>

        {/* Become Retailer/Wholesaler Links */}
        <div className="mt-8 px-4 space-y-3">
          <DisplayForUserType userTypes={[USER_TYPE.guest]}>
            <div
              className="text-lg font-medium underline capitalize cursor-pointer text-tertiary-600 hover:text-blue-700 transition-colors duration-200"
              onClick={() => {
                navigate('/signup/retailer')
                onClose()
              }}
            >
              Become a retailer
            </div>
          </DisplayForUserType>
          <DisplayForUserType userTypes={[USER_TYPE.guest]}>
            <div
              className="text-lg font-medium underline capitalize cursor-pointer text-tertiary-600 hover:text-blue-700 transition-colors duration-200"
              onClick={() => {
                navigate('/signup/wholesaler')
                onClose()
              }}
            >
              Become a wholesaler
            </div>
          </DisplayForUserType>
        </div>

        {/* Logout Button for Authenticated Users */}
        {isAuthenticated() && (
          <button
            className="absolute bottom-0 w-full py-4 font-medium text-red-600 bg-red-50 hover:bg-red-100 hover:shadow-lg transition-all duration-300"
            onClick={handleLogout}
          >
            Logout
          </button>
        )}
      </div>

      {/* Backdrop */}
      {open && <div className="fixed inset-0 bg-black bg-opacity-50 z-[999]" onClick={onClose} />}
    </>
  )
}
